import { useEffect, useRef, useState } from 'react';
import { Article } from 'wasp/entities';
import { useBookmarks } from '../BookmarksContext';
import { useArticleEngagement } from '../ArticleEngagementContext';
import { useArticleFlags } from '../ArticleFlagContext';
import {
  BsBookmark,
  BsBookmarkFill,
  BsDownload,
  BsFlag,
  BsFlagFill,
  BsHeart,
  BsHeartFill,
  BsShare,
} from 'react-icons/bs';
import { DropdownMenu } from '../../components/ui';
import { routes } from 'wasp/client/router';
import { Button, IconButton } from '../../components/common';
import { useLocaleContext } from '../../client/contexts/LocaleContext';
import { cn } from '../../client/cn';
import { shareOrDownloadCard, shareOrCopyUrl } from '../../client/shareLinks';
import { buildArticleShareCardDownloadUrl } from '../../shared/shareCards';
import { config } from 'wasp/client';
import { formatCompactNumber } from '../../shared/utils';

const FLAG_REASONS = [
  'Not positive',
  'Misleading',
  'Off-topic',
  'Other',
] as const;

interface ArticleCardProps {
  article: Article & {
    likeCount?: number;
  };
  featured?: boolean;
}

const ARTICLE_ACTION_SHELL_CLASS_NAME =
  'inline-flex h-8 items-center rounded-full px-2.5 text-sm font-medium transition-colors';
const ARTICLE_NEUTRAL_ACTION_CLASS_NAME =
  'text-neutral-500 hover:bg-neutral-100 hover:text-primary dark:text-neutral-300 dark:hover:bg-neutral-800 dark:hover:text-primary';

export default function ArticleCard({
  article,
  featured = false,
}: ArticleCardProps) {
  const { toggleBookmark, isBookmarked, isAuthenticated } = useBookmarks();
  const { isLiked, getLikeCount, seedLikeCount, toggleLike, isLikePending } =
    useArticleEngagement();
  const { toggleFlag, isFlagged, isFlagPending } = useArticleFlags();
  const { locale } = useLocaleContext();
  const bookmarked = isBookmarked(article.id);
  const liked = isLiked(article.id);
  const flagged = isFlagged(article.id);
  const flagPending = isFlagPending(article.id);
  const displayLikeCount = getLikeCount(article.id, article.likeCount ?? 0);
  const compactLikeCount = formatCompactNumber(displayLikeCount);
  const likePending = isLikePending(article.id);
  const [actionFeedback, setActionFeedback] = useState<string | null>(null);
  const [isDownloadingCard, setIsDownloadingCard] = useState(false);
  const actionFeedbackTimeoutRef = useRef<number | null>(null);

  const showActionFeedback = (message: string) => {
    setActionFeedback(message);

    if (actionFeedbackTimeoutRef.current !== null) {
      window.clearTimeout(actionFeedbackTimeoutRef.current);
    }

    actionFeedbackTimeoutRef.current = window.setTimeout(() => {
      setActionFeedback(null);
      actionFeedbackTimeoutRef.current = null;
    }, 2000);
  };

  useEffect(() => {
    seedLikeCount(article.id, article.likeCount ?? 0);
  }, [article.id, article.likeCount, seedLikeCount]);

  useEffect(() => {
    return () => {
      if (actionFeedbackTimeoutRef.current !== null) {
        window.clearTimeout(actionFeedbackTimeoutRef.current);
      }
    };
  }, []);

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getCategoriesToDisplay = () => {
    switch (locale) {
      case 'de':
        return article.germanCategories || [];
      case 'en':
      case 'all':
        return article.englishCategories || [];
      default:
        return article.englishCategories || [];
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if (
      (e.target as HTMLElement).closest('button') ||
      (e.target as HTMLElement).closest('a')
    ) {
      return;
    }

    window.open(article.link, '_blank', 'noopener,noreferrer');
  };

  const handleLikeClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    void toggleLike(article.id, displayLikeCount);
  };

  const handleFlagSelect = (reason: string) => {
    if (!isAuthenticated) {
      window.location.href = routes.LoginRoute.to;
      return;
    }
    void toggleFlag(article.id, reason);
  };

  const handleShareClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    void shareOrCopyUrl({
      title: article.title,
      text: article.summary,
      url: article.link,
    })
      .then((result) => {
        if (result === 'copied') {
          showActionFeedback('Link copied');
        }
      })
      .catch(() => {
        showActionFeedback('Could not copy link');
      });
  };

  const handleShareCardClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDownloadingCard(true);

    void shareOrDownloadCard({
      url: `${config.apiUrl}${buildArticleShareCardDownloadUrl(article.id)}`,
      fileName: `optimistic-news-article-${article.id}.png`,
      title: article.title,
      text: article.summary,
    })
      .then((result) => {
        if (result === 'downloaded') {
          showActionFeedback('Card saved');
        }
      })
      .catch(() => {
        showActionFeedback('Could not share card');
      })
      .finally(() => {
        setIsDownloadingCard(false);
      });
  };

  return (
    <div className="relative group h-full">
      {isAuthenticated ? (
        <IconButton
          icon={bookmarked ? <BsBookmarkFill /> : <BsBookmark />}
          aria-label={bookmarked ? 'Remove bookmark' : 'Bookmark article'}
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            void toggleBookmark(article);
          }}
          className={cn(
            'absolute top-2 right-2 z-10',
            bookmarked && 'text-primary hover:bg-primary hover:text-white',
          )}
        />
      ) : (
        <IconButton
          icon={<BsBookmark />}
          aria-label="Log in to bookmark"
          href={routes.LoginRoute.to}
          className="absolute top-2 right-2 z-10"
        />
      )}

      <article
        className={cn(
          'flex h-full cursor-pointer overflow-hidden rounded-lg border border-neutral-200 p-6 transition-all duration-200 hover:border-primary hover:shadow-lg dark:border-neutral-700 dark:hover:border-primary',
          featured ? 'flex-col md:flex-row md:gap-6' : 'flex-col',
          !featured && 'hover:scale-[1.02]',
        )}
        onClick={handleCardClick}
        role="link"
        tabIndex={0}
        aria-labelledby={`article-title-${article.id}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleCardClick(e as any);
          }
        }}
      >
        {article.imageUrl && (
          <div
            className={cn(
              'w-full',
              featured ? 'mb-4 md:mb-0 md:w-2/5 md:shrink-0' : 'mb-5',
            )}
          >
            <img
              src={article.imageUrl}
              alt={article.title}
              loading={featured ? 'eager' : 'lazy'}
              fetchPriority={featured ? 'high' : undefined}
              className={cn(
                'w-full rounded-lg object-cover',
                featured ? 'h-48 md:h-full md:min-h-[240px]' : 'h-48',
              )}
            />
          </div>
        )}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex flex-wrap gap-1.5 text-xs">
            {getCategoriesToDisplay().map((category) => (
              <span
                key={category}
                className="relative z-10 rounded-full bg-primary-50 px-2.5 py-0.5 text-[11px] font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
              >
                {category}
              </span>
            ))}
          </div>
          {article.sentiment >= 5 && (
            <div className="mt-2 inline-flex items-center gap-1 self-start rounded-full bg-primary/15 px-2.5 py-1 text-[11px] font-semibold tracking-wide text-primary-700 dark:bg-primary/20 dark:text-primary-300">
              <span aria-hidden="true">✦</span>
              Very Positive
            </div>
          )}
          <div className={cn('flex-1', featured ? 'mt-3' : 'mt-4')}>
            <h3
              id={`article-title-${article.id}`}
              className={cn(
                'font-semibold leading-snug text-neutral-900 group-hover:text-primary dark:text-white dark:group-hover:text-primary',
                featured ? 'text-xl sm:text-2xl' : 'text-lg',
              )}
            >
              {article.title}
            </h3>
            <p
              className={cn(
                'mt-2 text-sm leading-relaxed text-neutral-600 dark:text-neutral-300',
                featured ? 'line-clamp-4' : 'line-clamp-3',
              )}
            >
              {article.summary}
            </p>
          </div>
          <div className="mt-4 flex w-full items-center justify-between gap-x-3">
            <div className="flex items-center gap-3 text-xs text-neutral-500 dark:text-neutral-400">
              <span className="font-medium text-neutral-700 dark:text-neutral-200">
                {article.provider}
              </span>
              <span className="text-neutral-400 dark:text-neutral-500">
                &middot;
              </span>
              <time dateTime={article.publishedAt.toString()}>
                {formatDate(article.publishedAt)}
              </time>
            </div>
            <div className="-mr-2.5 flex items-center gap-1.5 sm:gap-2">
              {actionFeedback && (
                <span
                  className="text-xs text-neutral-500 dark:text-neutral-300"
                  role="status"
                  aria-live="polite"
                >
                  {actionFeedback}
                </span>
              )}
              <Button
                type="button"
                variant="ghost"
                tone="neutral"
                size="xs"
                onClick={handleShareCardClick}
                disabled={isDownloadingCard}
                className={cn(
                  ARTICLE_ACTION_SHELL_CLASS_NAME,
                  ARTICLE_NEUTRAL_ACTION_CLASS_NAME,
                  'gap-1.5 disabled:cursor-wait disabled:opacity-70',
                )}
                aria-label="Share card"
              >
                <BsDownload />
                <span className="hidden sm:inline">Card</span>
              </Button>
              <Button
                type="button"
                variant="ghost"
                tone="neutral"
                size="xs"
                onClick={handleShareClick}
                className={cn(
                  ARTICLE_ACTION_SHELL_CLASS_NAME,
                  ARTICLE_NEUTRAL_ACTION_CLASS_NAME,
                  'gap-1.5',
                )}
                aria-label="Share article"
              >
                <BsShare />
                <span className="hidden sm:inline">Share</span>
              </Button>
              <Button
                type="button"
                variant="ghost"
                tone={liked ? 'primary' : 'neutral'}
                size="xs"
                onClick={handleLikeClick}
                disabled={likePending}
                className={cn(
                  ARTICLE_ACTION_SHELL_CLASS_NAME,
                  'gap-1 tabular-nums active:scale-95 disabled:cursor-wait disabled:opacity-70',
                  liked
                    ? 'text-primary hover:bg-primary-50 dark:hover:bg-primary-950/40'
                    : ARTICLE_NEUTRAL_ACTION_CLASS_NAME,
                )}
                aria-label={liked ? 'Unlike article' : 'Like article'}
                aria-pressed={liked}
              >
                {liked ? (
                  <BsHeartFill aria-hidden="true" />
                ) : (
                  <BsHeart aria-hidden="true" />
                )}
                <span aria-hidden="true">{compactLikeCount}</span>
              </Button>
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <button
                    type="button"
                    disabled={flagPending}
                    className={cn(
                      ARTICLE_ACTION_SHELL_CLASS_NAME,
                      'active:scale-95 disabled:cursor-wait disabled:opacity-70',
                      flagged
                        ? 'text-danger-500 hover:bg-danger-50 dark:hover:bg-danger-950/40'
                        : ARTICLE_NEUTRAL_ACTION_CLASS_NAME,
                    )}
                    aria-label={flagged ? 'Remove flag' : 'Flag article'}
                    aria-pressed={flagged}
                  >
                    {flagged ? (
                      <BsFlagFill aria-hidden="true" />
                    ) : (
                      <BsFlag aria-hidden="true" />
                    )}
                  </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content
                    align="end"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {flagged ? (
                      <DropdownMenu.Item
                        className="text-danger-600 data-[highlighted]:bg-danger-500/10 data-[highlighted]:text-danger-600 dark:text-danger-400 dark:data-[highlighted]:bg-danger-500/20 dark:data-[highlighted]:text-danger-400"
                        onSelect={() => handleFlagSelect('remove')}
                      >
                        Remove flag
                      </DropdownMenu.Item>
                    ) : (
                      FLAG_REASONS.map((reason) => (
                        <DropdownMenu.Item
                          key={reason}
                          onSelect={() => handleFlagSelect(reason)}
                        >
                          {reason}
                        </DropdownMenu.Item>
                      ))
                    )}
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}
